import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearCommentId, clearConfirmTypeModal, setCommentId, setConfirmTypeModal } from "../../features/gms-state-and-modal/modal-slice";
import { useAddCommentProposalMutation, useDeleteCommentProposalMutation, useGetGroupChatProposalQuery, useGetCommentAttachmentMutation, useUpdateCommentProposalMutation } from "../../api/gms/proposal-chat/proposal-chats-api";
import { ChatAttachment } from "../../components/module/proposal-status/modal/preview-proposal/chats/chat-attachment-modal";
import { clearChatValue, selectChats, setChatValue } from "../../features/chats/chats-slice";
import { GroupChat } from "../../api/gms/proposal-chat/proposal-chats-api-state";
import { useProposalTypeData } from "../proposal/use-proposal-type-data";
import { useToastify } from "../../library/toastify/toast-provider";
import { useModalAccess } from "../../components/global/modal/use-modal-access";

export const useGroupChat = () => {

    const { showToastNotify } = useToastify();
    const dispatch = useDispatch();
    const {
        isDeleteMessage,
        isCancelUpdateMessage,
        confirmModal,
        currentProposalId,
        commentId
    } = useModalAccess()
    const { isDraftProposal } = useProposalTypeData()
    const {
        searchTerm, 
        message, 
        activeMessageId, 
        downloadingAttachmentId, 
        selectedFile, 
        showAttachments,
        isSearchOpen,
    } = useSelector(selectChats)

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const chatEndRef = useRef<HTMLDivElement | null>(null);

    const { data: groupMessages = [], isLoading: isLoadingGroupMessages, isError: isErrorGroupMessages } = useGetGroupChatProposalQuery(
        { proposalID: currentProposalId! },
        { skip: !currentProposalId }
    );

    const messages = useMemo<GroupChat[]>(() => {return groupMessages ?? []}, [groupMessages]);
    
    const filteredMessages = useMemo(() => {
        const search = searchTerm.trim().toLowerCase();
        if (!search) {
            return messages;
        }

        return messages.filter((msg) => {
            const messageText = msg.comment_text?.toLowerCase() ?? '';
            const commenter = msg.commenter_name?.toLowerCase() ?? '';
            const department = msg.department_name?.toLowerCase() ?? '';
            const role = msg.role_name?.toLowerCase() ?? '';
            const attachmentName = msg.attachment_name?.toLowerCase() ?? '';

            return (
                messageText.includes(search) ||
                commenter.includes(search) ||
                department.includes(search) ||
                role.includes(search) ||
                attachmentName.includes(search)
            );
        });
    }, [messages, searchTerm]);
    
    const allAttachments = useMemo<ChatAttachment[]>(() => {
        return messages
            .filter((msg) =>
                    Boolean(msg.attachment_path) &&
                    Boolean(msg.attachment_name) &&
                    !msg.is_deleted,
            )
            .map((msg) => ({
                id: msg.comment_id,
                messageId: msg.comment_id,
                name: msg.attachment_name as string,
                type: '',
                size: '',
                url: msg.attachment_path as string,
                uploadedBy: msg.commenter_name,
                uploadedAt: msg.created_at,
            }));
    }, [messages]);
    
    const [addCommentProposal, { isLoading: isLoadingAddCommentChat }] = useAddCommentProposalMutation();
    const [deleteCommentProposal, { isLoading: isLoadingDeleteComment }] = useDeleteCommentProposalMutation();
    const [updateCommentProposal, { isLoading: isLoadingUpdateComment }] = useUpdateCommentProposalMutation();
    const [getCommentAttachment] = useGetCommentAttachmentMutation();
    
    const handleSendMessage = async (attachment?: File | null) => {
        const trimmedMessage = message.trim();
        if (!trimmedMessage && !attachment) return false;
        if (!currentProposalId) return false;

        try {
            await addCommentProposal({
                proposalId: currentProposalId,
                ...(trimmedMessage ? { comment_text: trimmedMessage } : {}),
                ...(attachment ? { attachment } : {}),
            }).unwrap();

            dispatch(clearChatValue('message'));
            return true;
        } catch (error) {
            showToastNotify('Failed to send message', 'error')
            return false;
        }
    };

    const handleStartUpdateComment = (id: number, text: string | null | undefined) => {
        dispatch(setCommentId(id));
        dispatch(setChatValue({ key: 'message', value: text ?? '' }));
        dispatch(setConfirmTypeModal("isCancelUpdateMessage"));
        dispatch(clearChatValue('activeMessageId'));
    };

    const handleUpdateComment = async () => {
        const trimmedMessage = message.trim();
        if (!commentId) {
            showToastNotify('Comment ID is missing', 'warning')
            return false;
        }
        if (!trimmedMessage) {
            showToastNotify('Updated message cannot be empty', 'warning')
            return false;
        }

        try {
            await updateCommentProposal({ commentId, comment_text: trimmedMessage }).unwrap();
            dispatch(clearChatValue('message'));
            dispatch(setCommentId(null));
            dispatch(clearConfirmTypeModal());
            return true;
        } catch (error) {
            showToastNotify('Failed to update message', 'error')
            return false;
        }
    };

    const handleCancelUpdateComment = () => {
        dispatch(setCommentId(null));
        dispatch(clearConfirmTypeModal());
        dispatch(clearChatValue('message'));
    };

    const handleDeleteComment = async () => {
        if (!commentId) {
            showToastNotify('Comment ID is missing', 'warning')
            return false;
        }

        try {
            await deleteCommentProposal({ commentId }).unwrap();
            dispatch(setCommentId(null));
            dispatch(clearConfirmTypeModal());
            dispatch(clearChatValue('activeMessageId'));
            return true;
        } catch (error) {
            showToastNotify('Failed to delete message', 'error')
            return false;
        }
    };

    const handleDownloadAttachment = async (attachment: ChatAttachment) => {
        if (!attachment.messageId) {
            return;
        }
        dispatch(setChatValue({ key: 'downloadingAttachmentId', value: attachment.messageId }));
        try {
            const blob = await getCommentAttachment({
                commentId: attachment.messageId,
            }).unwrap();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = attachment.name;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            showToastNotify('Failed to download attachment', 'error')
        } finally {
            dispatch(clearChatValue('downloadingAttachmentId'));
        }
    };


    const handleSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        dispatch(setChatValue({ key: 'selectedFile', value: file }));
        event.target.value = '';
    };

    const handleRemoveFile = () => {
        dispatch(clearChatValue('selectedFile'));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleAttachmentClick = () => {
        if (isDraftProposal) {
            return;
        }
        if (isCancelUpdateMessage) {
            return;
        }
        fileInputRef.current?.click();
    };

    const clearSearch = () => {
        dispatch(clearChatValue('searchTerm'));
    };

    const handleClose = () => {
        dispatch(clearConfirmTypeModal());
        dispatch(clearCommentId());
    };

    const handleFinalClick = async () => {
        if (isCancelUpdateMessage) {
            await handleUpdateComment();
            return;
        }
        const success = await handleSendMessage(selectedFile);
        if (success) {
            dispatch(clearChatValue('selectedFile'));
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    useEffect(() => {
        if (!groupMessages?.length) return;
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [groupMessages?.length]);

    return {
        showAttachments,
        isSearchOpen,
        groupMessages,
        isLoadingGroupMessages,
        isErrorGroupMessages,
        chatEndRef,
        fileInputRef,
        activeMessageId,
        message,
        handleSendMessage,
        handleStartUpdateComment,
        handleUpdateComment,
        handleCancelUpdateComment,
        handleDeleteComment,
        isLoadingAddCommentChat,
        isLoadingDeleteComment,
        isLoadingUpdateComment,
        deletingMessageId: commentId,
        currentConfirmTypeModal: confirmModal,
        handleClose,
        isOpenDeleteModal: isDeleteMessage,

        handleDownloadAttachment,
        downloadingAttachmentId,

        searchTerm,
        selectedFile,

        handleSelectFile,
        handleRemoveFile,
        handleAttachmentClick,
        handleFinalClick,
        clearSearch,

        filteredMessages,
        allAttachments,
    };
};